import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { GridPaginationModel } from '@mui/x-data-grid';
import { PAGINATION_DEFAULT_PAGE_SIZE } from 'config';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import {
  UnitSystem,
  useDeleteWearDataMutation,
  useGetInspectionPointQuery,
  useSearchWearDataQuery,
  WearDataDto
} from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WearDataEntryPanelPageParams } from '../WearDataForm/constants';
import WearDataTable from './WearDataTable';

const PREFIX = 'InspectionPointPanelPage';

const classes = {
  listContainer: `${PREFIX}-listContainer`,
};

const StyledBox = styled(Box)(() => ({
  [`& .${classes.listContainer}`]: {
    overflowX: 'hidden',
    overflowY: 'auto',
  },
}));

interface InspectionPointPanelPageProps {
  wearDataEntryPath: string;
  wearDataUpdatePath: string;
  unitSystemPreference: UnitSystem;
  inspectionPointId?: string;
  projectId?: string;
  onNewWearData?: () => void;
  onEditWearData?: (wearDataId: string) => void;
}

const InspectionPointPanelPage = (
  { wearDataEntryPath,
    wearDataUpdatePath,
    unitSystemPreference,
    inspectionPointId: propInspectionPointId,
    projectId: propProjectId,
    onNewWearData,
    onEditWearData }: InspectionPointPanelPageProps,
  ) => {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(PAGINATION_DEFAULT_PAGE_SIZE);
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { entityId: urlProjectId, inspectionPointId: urlInspectionPointId } = useParams<WearDataEntryPanelPageParams>();
  const projectId = (propProjectId || urlProjectId) as string;
  const inspectionPointId = (propInspectionPointId || urlInspectionPointId) as string;

  const deleteWearDataMutation = useDeleteWearDataMutation();

  // Get inspection point details to get the spool it measures
  const { data: inspectionPoint } = useGetInspectionPointQuery({
    projectId,
    inspectionPointId,
  });

  const {
    data: wearDataList,
    isFetching,
    isFetched,
    isPreviousData,
  } = useSearchWearDataQuery({
    mineModelId: projectId,
    inspectionPoint: inspectionPointId,
    page,
    pageSize,
  });

  const handlePaginationChange = (newPaginationState: GridPaginationModel) => {
    setPage(newPaginationState.page + 1);
    setPageSize(newPaginationState.pageSize);
  };

  const handleNewDataClick = () => {
    if (onNewWearData) {
      onNewWearData();
    } else {
      navigate(
        wearDataEntryPath
          .replace(':entityId', projectId)
          .replace(':inspectionPointId', inspectionPointId),
      );
    }
  };

  const handleEdit = (wearDataId: string) => {
    if (onEditWearData) {
      onEditWearData(wearDataId);
    } else {
      navigate(
        wearDataUpdatePath
          .replace(':entityId', projectId)
          .replace(':inspectionPointId', inspectionPointId)
          .replace(':wearDataId', wearDataId),
      );
    }
  };

  const handleDelete = (wearData: WearDataDto) => {
    confirm({
      variant: 'danger',
      description: i18n._('Are you sure you want to delete this wear data?'),
    }).then(() => deleteWearDataMutation.mutate({ projectId, entityId: wearData.entityId }));
  };

  return (
    <StyledBox
      sx={{
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
      }}
    >
      <Box sx={{
        mb: 4,
      }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            aria-label={i18n._('add new client')}
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleNewDataClick}
          >
            <Trans id="New Wear Data">New Wear Data</Trans>
          </Button>
        </Box>
      </Box>
      <Box
        className={classes.listContainer}
        sx={{
          flex: 1,
          pb: 2,
          borderRadius: '8px',
        }}
      >
        <WearDataTable
          data={wearDataList?.items || []}
          page={page}
          pageSize={pageSize}
          totalItems={wearDataList?.pager?.totalItems || 0}
          loading={isFetching}
          loadingNew={isFetching && (!isFetched || isPreviousData)}
          onPaginationModelChange={handlePaginationChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          searchTerm=""
          unitSystemPreference={unitSystemPreference}
          mineModelId={projectId}
          spoolId={inspectionPoint?.spoolId || ''}
        />
      </Box>
    </StyledBox>
  );
};

export default InspectionPointPanelPage;
